import React, { useState, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from "@material-ui/core/styles";
import Badge from '@material-ui/core/Badge';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import RefreshIcon from '@material-ui/icons/Refresh';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from './firebase';
import { getBadgeContent, getImageUrl, isComicOwned } from './utils';
import { updateSeriesModifiedDate, displaySeriesTitle } from './services/seriesService';

const useStyles = makeStyles(theme => ({
    webPlant: {
        padding: '10px 10px 5px 5px',
        display: 'inline-flex'
    },
    separator: {
        paddingTop: '10px'
    },
    seriesContainer: {
        marginBottom: '20px',
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '5px'
    },
    seriesTitle: {
        fontSize: '1.2em',
        fontWeight: 'bold',
        marginBottom: '10px'
    },
    heroName: {
        fontSize: '0.9em',
        color: '#666',
        marginBottom: '5px'
    },
    filterContainer: {
        padding: '10px',
        display: 'flex',
        gap: '20px',
        flexWrap: 'wrap',
        marginBottom: '20px'
    },
    formControl: {
        minWidth: 200
    },
    sectionTitle: {
        fontSize: '1.3em',
        fontWeight: 'bold',
        marginTop: '10px',
        marginBottom: '10px',
        padding: '10px',
        backgroundColor: '#f5f5f5',
        borderRadius: '5px'
    },
    sideBySideContainer: {
        display: 'flex',
        gap: '20px',
        alignItems: 'flex-start',
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column'
        }
    },
    leftSection: {
        flex: '1 1 50%',
        minWidth: 0
    },
    rightSection: {
        flex: '1 1 50%',
        minWidth: 0
    },
    reloadSection: {
        padding: '15px',
        border: '2px solid #2196f3',
        borderRadius: '5px',
        backgroundColor: '#f0f8ff',
        height: '100%'
    },
    reloadSeriesItem: {
        padding: '10px',
        marginBottom: '10px',
        backgroundColor: 'white',
        borderRadius: '3px',
        border: '1px solid #ddd'
    },
    reloadButton: {
        marginTop: '10px'
    },
    clickableSeriesTitle: {
        fontWeight: 'bold',
        cursor: 'pointer',
        color: '#1976d2',
        '&:hover': {
            textDecoration: 'underline',
            color: '#1565c0'
        }
    }
}));

const QuickEntry = (props) => {
    const classes = useStyles();
    const [seriesWithUnownedComics, setSeriesWithUnownedComics] = useState([]);
    const [seriesToReload, setSeriesToReload] = useState([]);
    const [selectedSeriesToReload, setSelectedSeriesToReload] = useState({});
    const [loading, setLoading] = useState(true);
    const [reloading, setReloading] = useState(false);
    const [selectedHero, setSelectedHero] = useState('all');
    const [limit, setLimit] = useState(20);

    // Destructure props for useCallback dependencies
    const { appuser, comicsIGot, addComics, updateIGotStatus } = props;

    // Keep comicsIGot in a ref so changes to it don't cause loadRecentSeries to re-run.
    // This prevents the screen from reloading when the user clicks a comic to mark it as owned.
    const comicsIGotRef = React.useRef(comicsIGot);
    const hasLoadedWithDataRef = React.useRef(false);

    useEffect(() => {
        comicsIGotRef.current = comicsIGot;
    }, [comicsIGot]);

    /**
     * Get the effective date for a comic, accounting for cover date vs actual on-sale date.
     * Cover dates are typically 6-8 weeks ahead of actual on-sale dates.
     * 
     * @param {Object} comicData - The comic data object
     * @returns {Date|null} - The effective date to use for filtering
     */
    const getEffectiveComicDate = (comicData) => {
        // First priority: Use the modified date if available (when comic was added/updated)
        if (comicData.modified) {
            return new Date(comicData.modified);
        }

        // Second priority: Use date_last_updated if available
        if (comicData.date_last_updated) {
            return new Date(comicData.date_last_updated);
        }

        // Fallback: Use cover date minus 6 weeks (42 days)
        const onsaleDate = comicData.dates && comicData.dates.find(d => d.type === 'onsaleDate');
        if (onsaleDate && onsaleDate.date) {
            const coverDate = new Date(onsaleDate.date);
            // Subtract 6 weeks (42 days) to get approximate actual on-sale date
            const effectiveDate = new Date(coverDate.getTime() - (42 * 24 * 60 * 60 * 1000));
            return effectiveDate;
        }

        return null;
    };

    const loadRecentSeries = React.useCallback(async () => {
        if (!appuser) return;

        setLoading(true);
        const db = firestore;
        const withUnownedComics = [];
        const needsReload = [];
        const now = new Date();
        const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);

        // Get all heroes for the user
        const heroesSnapshot = await getDocs(collection(db, "users/" + appuser.email + "/heroes/"));

        // Use the ref so changes to comicsIGot don't cause a full reload
        const ownedComics = comicsIGotRef.current;
        if (ownedComics.length > 0) {
            hasLoadedWithDataRef.current = true;
        }

        // Process all heroes in parallel
        const heroPromises = heroesSnapshot.docs.map(async (heroDoc) => {
            const heroName = heroDoc.data().name;
            const heroData = heroDoc.data().data;

            // Get all series for this hero
            const seriesSnapshot = await getDocs(
                collection(db, "users/" + appuser.email + "/heroes/" + heroName + "/series")
            );

            // Process all series in parallel
            const seriesPromises = seriesSnapshot.docs.map(async (seriesDoc) => {
                const series = seriesDoc.data().data;
                const seriesTitle = seriesDoc.data().title;
                const seriesModified = seriesDoc.data().modified;

                // Check if series was recently active (reload or comic ownership) within 90 days
                const seriesInWindow = seriesModified ? new Date(seriesModified) >= ninetyDaysAgo : false;

                // Get all comics for this series
                const comicsSnapshot = await getDocs(
                    collection(db, "users/" + appuser.email + "/heroes/" + heroName + "/series/" + seriesTitle + "/comics")
                );

                const comics = [];
                let mostRecentComicDate = null;

                comicsSnapshot.forEach((comicDoc) => {
                    const docData = comicDoc.data();
                    const comicData = docData.data;
                    // Use document-level modified date if available, otherwise use the one in nested data
                    const comicWithModified = {
                        ...comicData,
                        modified: docData.modified || comicData.modified
                    };
                    const effectiveDate = getEffectiveComicDate(comicWithModified);

                    // Track the most recent comic date for this series
                    if (effectiveDate && (!mostRecentComicDate || effectiveDate > mostRecentComicDate)) {
                        mostRecentComicDate = effectiveDate;
                    }

                    // Filter: include comic if it has a date in the 90-day window,
                    // OR if the series itself is in the window (catches comics with no dates)
                    const comicInWindow = effectiveDate && effectiveDate <= now && effectiveDate >= ninetyDaysAgo;
                    if (comicInWindow || seriesInWindow) {
                        comics.push({
                            ...comicWithModified,
                            gotIt: isComicOwned(comicData, ownedComics),
                            // Use comic date, falling back to series modified date for sorting
                            onsaleDate: effectiveDate || (seriesModified ? new Date(seriesModified) : null)
                        });
                    }
                });

                // Skip series if neither recent comics nor recent series activity
                if (comics.length === 0 && !seriesInWindow) {
                    return null;
                }

                // Sort by issue number and find the first one not "got"
                comics.sort((a, b) => a.issueNumber - b.issueNumber);
                const nextComic = comics.find(comic => !comic.gotIt);

                // Use series modified date as fallback for mostRecentComicDate (for sorting)
                const effectiveMostRecentDate = mostRecentComicDate || (seriesModified ? new Date(seriesModified) : null);

                const seriesItem = {
                    heroName: heroName,
                    heroData: heroData,
                    series: series,
                    seriesTitle: seriesTitle,
                    allComics: comics,
                    mostRecentComicDate: effectiveMostRecentDate
                };

                if (nextComic) {
                    // Has unowned comics - add to quick entry section
                    return {
                        ...seriesItem,
                        nextComic: nextComic,
                        type: 'unowned'
                    };
                } else {
                    // All comics owned - add to reload section
                    return {
                        ...seriesItem,
                        type: 'reload'
                    };
                }
            });

            return Promise.all(seriesPromises);
        });

        const allHeroResults = await Promise.all(heroPromises);
        const flatResults = allHeroResults.flat().filter(Boolean);

        // Separate into two categories
        flatResults.forEach(item => {
            if (item.type === 'unowned') {
                withUnownedComics.push(item);
            } else if (item.type === 'reload') {
                needsReload.push(item);
            }
        });

        // Sort both arrays alphabetically by hero name, then by on sale date
        const sortFunction = (a, b) => {
            const heroComparison = a.heroName.localeCompare(b.heroName);
            if (heroComparison !== 0) return heroComparison;

            const dateA = a.nextComic ? a.nextComic.onsaleDate : (a.mostRecentComicDate || new Date(0));
            const dateB = b.nextComic ? b.nextComic.onsaleDate : (b.mostRecentComicDate || new Date(0));
            return dateA - dateB;
        };

        withUnownedComics.sort(sortFunction);
        needsReload.sort(sortFunction);

        setSeriesWithUnownedComics(withUnownedComics);
        setSeriesToReload(needsReload);
        setLoading(false);
    }, [appuser]);

    useEffect(() => {
        loadRecentSeries();
    }, [loadRecentSeries]);

    // When comicsIGot first becomes available after an empty initial load
    // (e.g. user navigates directly to /quickentry/ before Firebase data arrives),
    // trigger a reload so ownership is reflected correctly.
    useEffect(() => {
        if (comicsIGot.length > 0 && !hasLoadedWithDataRef.current) {
            loadRecentSeries();
        }
    }, [comicsIGot, loadRecentSeries]);

    // Get unique heroes from both series lists
    const uniqueHeroes = useMemo(() => {
        const heroes = new Set();
        [...seriesWithUnownedComics, ...seriesToReload].forEach(item => {
            if (item.heroName) {
                heroes.add(item.heroName);
            }
        });
        return Array.from(heroes).sort();
    }, [seriesWithUnownedComics, seriesToReload]);

    // Filter and limit series with unowned comics
    const displayedSeries = useMemo(() => {
        const filtered = selectedHero === 'all'
            ? seriesWithUnownedComics
            : seriesWithUnownedComics.filter(item => item.heroName === selectedHero);

        return filtered.slice(0, limit);
    }, [seriesWithUnownedComics, selectedHero, limit]);

    // Filter series to reload
    const displayedReloadSeries = useMemo(() => {
        return selectedHero === 'all'
            ? seriesToReload
            : seriesToReload.filter(item => item.heroName === selectedHero);
    }, [seriesToReload, selectedHero]);

    const handleComicClick = (seriesItem, comic) => {
        // Toggle the "got it" status - use isComicOwned for proper matching
        const isGot = isComicOwned(comic, comicsIGot);
        const newGotItStatus = !isGot;

        // Update local state immediately to prevent screen from clearing
        setSeriesWithUnownedComics(prevSeries =>
            prevSeries.map(series => {
                if (series.series.id === seriesItem.series.id &&
                    (series.heroData.id || 0) === (seriesItem.heroData.id || 0)) {
                    // Update the gotIt status of the current comic
                    const updatedAllComics = series.allComics.map(c =>
                        c.id === comic.id ? { ...c, gotIt: newGotItStatus } : c
                    );

                    // Find the next unowned comic
                    const nextUnowned = updatedAllComics.find(c => !c.gotIt);

                    // If there's no next unowned comic, remove this series from the list
                    if (!nextUnowned) {
                        return null;
                    }

                    return {
                        ...series,
                        nextComic: nextUnowned,
                        allComics: updatedAllComics
                    };
                }
                return series;
            }).filter(Boolean) // Remove series with no unowned comics
        );

        // Update Firebase in the background - pass the comic object for proper ID storage
        updateIGotStatus(comic, newGotItStatus, seriesItem.series.id, seriesItem.heroData.id || 0);

        // Update the series modified date so it remains in the 90-day window
        if (appuser) {
            updateSeriesModifiedDate(seriesItem.heroName, seriesItem.seriesTitle, appuser.email)
                .catch(err => console.error('Error updating series modified date:', err));
        }
    };

    const handleHeroChange = (event) => {
        setSelectedHero(event.target.value);
    };

    const handleLimitChange = (event) => {
        setLimit(event.target.value);
    };

    const handleSeriesCheckboxChange = (seriesKey) => {
        setSelectedSeriesToReload(prev => ({
            ...prev,
            [seriesKey]: !prev[seriesKey]
        }));
    };

    const handleReloadSelected = async () => {
        if (!addComics) return;

        setReloading(true);

        try {
            const seriesToReloadList = displayedReloadSeries.filter((_, index) => {
                const seriesKey = `${_.heroName}-${_.seriesTitle}`;
                return selectedSeriesToReload[seriesKey];
            });

            for (const seriesItem of seriesToReloadList) {
                try {
                    console.log(`Reloading series: ${seriesItem.seriesTitle} for hero: ${seriesItem.heroName}`);
                    await addComics(seriesItem.series, seriesItem.heroData);
                } catch (error) {
                    console.error(`Error reloading series ${seriesItem.seriesTitle}:`, error);
                }
            }

            // Refresh the series list after reloading
            await loadRecentSeries();
            setSelectedSeriesToReload({});
        } catch (error) {
            console.error('Error during series reload:', error);
        } finally {
            setReloading(false);
        }
    };

    const handleSelectAllReload = () => {
        const allSelected = {};
        displayedReloadSeries.forEach(item => {
            const seriesKey = `${item.heroName}-${item.seriesTitle}`;
            allSelected[seriesKey] = true;
        });
        setSelectedSeriesToReload(allSelected);
    };

    const handleDeselectAllReload = () => {
        setSelectedSeriesToReload({});
    };

    const handleSeriesClick = (seriesItem) => {
        // Find the hero card by name
        const heroCard = props.cards && props.cards.find(card => card.name === seriesItem.heroName);

        if (heroCard && props.setCurrentCard && props.setCurrentSeries) {
            // Set the current hero
            props.setCurrentCard(heroCard);

            // Set the current series
            if (seriesItem.series) {
                props.setCurrentSeries(seriesItem.series, heroCard);
            }

            // Navigate to the series page
            props.history.push('/series/');
        }
    };

    if (loading) {
        return <div>Loading recent series...</div>;
    }

    if (seriesWithUnownedComics.length === 0 && seriesToReload.length === 0) {
        return <div>No series found. Start adding some comics to your collection!</div>;
    }

    const selectedReloadCount = Object.values(selectedSeriesToReload).filter(Boolean).length;

    return (
        <div>
            <h2>Quick Entry - Recent Series</h2>
            <p>Manage your recent comics from the last 90 days.</p>

            <div className={classes.filterContainer}>
                <FormControl className={classes.formControl}>
                    <InputLabel id="hero-filter-label">Filter by Hero</InputLabel>
                    <Select
                        labelId="hero-filter-label"
                        id="hero-filter"
                        value={selectedHero}
                        onChange={handleHeroChange}
                    >
                        <MenuItem value="all">All Heroes</MenuItem>
                        {uniqueHeroes.map(hero => (
                            <MenuItem key={hero} value={hero}>{hero}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl className={classes.formControl}>
                    <InputLabel id="limit-label">Comics to Display</InputLabel>
                    <Select
                        labelId="limit-label"
                        id="limit"
                        value={limit}
                        onChange={handleLimitChange}
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={30}>30</MenuItem>
                        <MenuItem value={40}>40</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                    </Select>
                </FormControl>
            </div>

            {/* Side by side layout */}
            <div className={classes.sideBySideContainer}>
                {/* Left section - Comics with unowned issues */}
                <div className={classes.leftSection}>
                    {displayedSeries.length > 0 && (
                        <>
                            <h3 className={classes.sectionTitle}>Comics to Add ({displayedSeries.length})</h3>
                            <p>Click on a comic to toggle ownership status.</p>

                            {displayedSeries.map((item, index) => (
                                <div key={index} className={classes.seriesContainer}>
                                    <div className={classes.heroName}>{item.heroName}</div>
                                    <div className={classes.seriesTitle}>{displaySeriesTitle(item.seriesTitle)}</div>
                                    <div>Next Issue: #{item.nextComic.issueNumber}</div>
                                    <Grid container className={classes.separator}>
                                        <Grid item className={classes.webPlant}>
                                            <div className={item.nextComic.gotIt ? "cardwrapper_highlight" : "cardwrapper_nohighlight"}>
                                                <Badge badgeContent={getBadgeContent(item.nextComic.issueNumber, item.nextComic.variantDescription)} color="primary" max={9999}>
                                                    <img
                                                        src={getImageUrl(item.nextComic, 'portrait_medium')}
                                                        title={item.nextComic.title}
                                                        onClick={() => handleComicClick(item, item.nextComic)}
                                                        alt={item.nextComic.title}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                </Badge>
                                            </div>
                                        </Grid>
                                    </Grid>
                                </div>
                            ))}
                        </>
                    )}
                    {displayedSeries.length === 0 && (
                        <div>
                            <h3 className={classes.sectionTitle}>Comics to Add (0)</h3>
                            <p>No comics with unowned issues in the last 90 days.</p>
                        </div>
                    )}
                </div>

                {/* Right section - Series that need reloading */}
                <div className={classes.rightSection}>
                    {displayedReloadSeries.length > 0 && (
                        <div className={classes.reloadSection}>
                            <h3 className={classes.sectionTitle}>Series to Check for New Issues ({displayedReloadSeries.length})</h3>
                            <p>These series have all comics marked as owned. Select series to check Comic Vine for new issues.</p>

                            <div style={{ marginBottom: '10px' }}>
                                <Button
                                    size="small"
                                    onClick={handleSelectAllReload}
                                    disabled={reloading}
                                >
                                    Select All
                                </Button>
                                <Button
                                    size="small"
                                    onClick={handleDeselectAllReload}
                                    disabled={reloading}
                                    style={{ marginLeft: '10px' }}
                                >
                                    Deselect All
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleReloadSelected}
                                    disabled={reloading || selectedReloadCount === 0}
                                    startIcon={reloading ? <CircularProgress size={20} /> : <RefreshIcon />}
                                    className={classes.reloadButton}
                                    style={{ marginLeft: '10px' }}
                                >
                                    {reloading ? `Reloading...` : `Reload Selected (${selectedReloadCount})`}
                                </Button>
                            </div>

                            {displayedReloadSeries.map((item, index) => {
                                const seriesKey = `${item.heroName}-${item.seriesTitle}`;
                                return (
                                    <div key={seriesKey} className={classes.reloadSeriesItem}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!selectedSeriesToReload[seriesKey]}
                                                    onChange={() => handleSeriesCheckboxChange(seriesKey)}
                                                    disabled={reloading}
                                                />
                                            }
                                            label={
                                                <div>
                                                    <div
                                                        className={classes.clickableSeriesTitle}
                                                        onClick={() => handleSeriesClick(item)}
                                                    >
                                                        {displaySeriesTitle(item.seriesTitle)}
                                                    </div>
                                                    <div style={{ fontSize: '0.9em', color: '#666' }}>{item.heroName}</div>
                                                    <div style={{ fontSize: '0.85em', color: '#999' }}>
                                                        {item.allComics.length} comic{item.allComics.length !== 1 ? 's' : ''} in last 90 days (all owned)
                                                    </div>
                                                </div>
                                            }
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {displayedReloadSeries.length === 0 && (
                        <div className={classes.reloadSection}>
                            <h3 className={classes.sectionTitle}>Series to Check for New Issues (0)</h3>
                            <p>No series with all comics owned in the last 90 days.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default withRouter(QuickEntry);
